import React from 'react';
import { ValidatorForm } from 'react-material-ui-form-validator';

import useIsFormDirty from '@client/hooks/useIsFormDirty';
import { usePageStore } from '@client/store/pageStore/context/pageContext';
import i18n from '@client/utils/i18n';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(() => ({
    buttonContainer: {
        position: 'sticky',
        bottom: '10px',
        border: '2px solid rgba(0, 0, 0, 0.25)',
        background: 'rgba(255, 255, 255, 0.9)',
        borderRadius: '8px',
        margin: '20px 0 10px 20px',
        padding: '10px',
        zIndex: 1000,
        width: '100%'
    }
}));

interface LediumFormProps {
    header?: React.ReactNode;
    children?: React.ReactNode;
    formRef?: React.RefObject<unknown>;
    onSubmit: () => void;
    onCancel?: () => void;
    saveDisabled?: boolean;
    showButtons?: boolean;
    saveText?: React.ReactNode;
    cancelText?: React.ReactNode;
    additionalButton?: React.ReactNode;
    additionalFormData?: Record<string, unknown>;
    resetKey?: unknown;
}

export const LediumForm = ({
    header,
    children,
    formRef,
    onSubmit,
    onCancel,
    saveDisabled = false,
    showButtons = true,
    saveText = i18n('save'),
    cancelText = i18n('cancel'),
    additionalButton,
    additionalFormData,
    resetKey
}: LediumFormProps) => {
    const {
        pageState: { formData, isFormSubmitting }
    } = usePageStore();

    const classes = useStyles();
    const isDirty = useIsFormDirty({ ...formData, ...additionalFormData }, resetKey);
    const buttons: React.ReactNode[] = [];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (additionalButton) {
        buttons.push(additionalButton);
    }
    if (onCancel) {
        buttons.push(
            <Button variant="contained" onClick={onCancel} startIcon={<CancelIcon />}>
                {cancelText}
            </Button>
        );
    }

    if (!formData.readOnly) {
        buttons.push(
            <Button
                variant="contained"
                color={onCancel ? 'primary' : 'inherit'}
                type="submit"
                startIcon={onCancel ? <SaveIcon /> : <CheckIcon />}
                disabled={saveDisabled || !isDirty || isFormSubmitting}
            >
                {saveText}
            </Button>
        );
    }

    return (
        <ValidatorForm onSubmit={onSubmit} ref={formRef ? formRef : null}>
            <Grid container spacing={3}>
                {header ? header : null}
                {children}
                {showButtons && (
                    <div className={classes.buttonContainer}>
                        <Stack style={{ width: '100%' }} direction={isMobile ? 'column' : 'row'} justifyContent="space-around" alignItems="center" spacing={4}>
                            {buttons.map((button, i) => (
                                <React.Fragment key={i}>{button}</React.Fragment>
                            ))}
                        </Stack>
                    </div>
                )}
            </Grid>
        </ValidatorForm>
    );
};
